/**
 * Button — the primary action primitive (design-revamp / handoff-2026-06).
 *
 * Production translation of the prototype `Button`
 * (`docs/GardEn/handoff-2026-06/garden/ui.jsx`). The prototype uses inline
 * styles; here every colour/space/radius is a Tailwind token utility or an
 * arbitrary `--g-*` value — never a literal hex or magic px.
 *
 * Variants:  primary | secondary | outline | ghost | destructive
 * Sizes:     sm (38px) | md (48px) | lg (54px)
 *
 * Loading shows the shared `.g-spin` ring (defined globally in
 * `apps/web/src/styles/tailwind.css`) and disables the button. Press
 * feedback is `active:scale-[0.98]` (the prototype's mouse-down transform,
 * expressed declaratively). Corner radius is `--g-radius`.
 */
import type { ButtonHTMLAttributes, ReactElement, ReactNode } from "react";

import { Icon, type IconName } from "./Icon";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "destructive"
  | "onPrimary"
  | "onPrimaryOutline";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** Visual variant. Defaults to `primary`. */
  variant?: ButtonVariant;
  /** Size token. Defaults to `md`. */
  size?: ButtonSize;
  /** Stretch to the full width of the container. */
  full?: boolean;
  /** Leading icon name (hidden while loading). */
  leading?: IconName;
  /** Trailing icon name (hidden while loading). */
  trailing?: IconName;
  /** Loading state — shows the `.g-spin` ring and disables interaction. */
  loading?: boolean;
  /** Button label / content. */
  children: ReactNode;
}

/**
 * Per-size geometry. Heights mirror the prototype (38/48/54). Icon px also
 * tracks the prototype (`lg` → 19, otherwise 17). Padding + text size use
 * Tailwind scale steps closest to the prototype values.
 */
const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-[38px] px-3.5 text-[13px] gap-1.5",
  md: "h-12 px-[18px] text-[14.5px] gap-2",
  lg: "h-[54px] px-[22px] text-base gap-2",
};

const iconPx: Record<ButtonSize, number> = {
  sm: 17,
  md: 17,
  lg: 19,
};

/**
 * Token-mapped variant classes. Mirrors the prototype's `variants` map:
 *   primary          → solid primary + on-primary text
 *   secondary        → soft primary tint + primary-ink text
 *   outline          → transparent + hairline border + ink text
 *   ghost            → transparent + ink text
 *   destructive      → solid error + white text
 *   onPrimary        → solid cream button for use ON a brand/photographic dark
 *                      surface (the landing hero teal scrim): cream fill, brand
 *                      ink. Colours are theme-stable — the scrim is always
 *                      `--g-primary`, `--g-on-primary` stays cream in light+dark,
 *                      and `--g-primary` keeps ~7:1 contrast on the cream fill
 *                      (whereas `--g-primary-ink` flips light in dark mode).
 *   onPrimaryOutline → its frosted translucent companion (secondary action on the
 *                      same dark surface): a visible cream border + cream text +
 *                      a faint cream wash so it reads as a button, not plain text.
 *
 * Each variant owns its focus-ring COLOUR (the shared base sets only the ring
 * width); the on-* variants ring in cream so the focus state stays visible on
 * the dark scrim.
 */
const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-[var(--g-on-primary)] border border-transparent hover:shadow-[var(--g-shadow-sm)] focus-visible:ring-[var(--g-primary-soft)]",
  secondary:
    "bg-[var(--g-primary-soft)] text-[var(--g-primary-ink)] border border-transparent focus-visible:ring-[var(--g-primary-soft)]",
  outline:
    "bg-transparent text-foreground border border-border hover:bg-[var(--g-primary-soft)] focus-visible:ring-[var(--g-primary-soft)]",
  ghost:
    "bg-transparent text-foreground border border-transparent hover:bg-[var(--g-primary-soft)] focus-visible:ring-[var(--g-primary-soft)]",
  destructive:
    "bg-[var(--g-error)] text-white border border-transparent focus-visible:ring-[var(--g-primary-soft)]",
  onPrimary:
    "bg-[var(--g-on-primary)] text-[var(--g-primary)] border border-transparent shadow-[var(--g-shadow-md)] hover:shadow-[var(--g-shadow-lg)] focus-visible:ring-[var(--g-on-primary)]/[0.7]",
  onPrimaryOutline:
    "bg-[var(--g-on-primary)]/[0.15] text-[var(--g-on-primary)] border border-[var(--g-on-primary)]/[0.7] backdrop-blur-sm hover:bg-[var(--g-on-primary)]/[0.26] focus-visible:ring-[var(--g-on-primary)]/[0.7]",
};

/**
 * Renders a token-styled button.
 *
 * `loading` takes precedence over the icons (the spinner replaces the
 * leading/trailing glyphs) and forces the disabled state. `data-testid`
 * is `g-button`; `data-variant` / `data-size` aid test selection.
 */
export function Button({
  variant = "primary",
  size = "md",
  full = false,
  leading,
  trailing,
  loading = false,
  disabled = false,
  type = "button",
  className,
  children,
  ...rest
}: ButtonProps): ReactElement {
  const isDisabled = disabled || loading;
  const classes = [
    "inline-flex",
    "items-center",
    "justify-center",
    "rounded-[var(--g-radius)]",
    "font-semibold",
    "tracking-[-0.01em]",
    "whitespace-nowrap",
    "select-none",
    "transition-[transform,background-color,box-shadow,opacity] duration-150 ease-[var(--g-ease)]",
    "active:scale-[0.98]",
    "focus-visible:outline-none focus-visible:ring-[3px]",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    full ? "w-full" : "",
    sizeClasses[size],
    variantClasses[variant],
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type={type}
      data-testid="g-button"
      data-variant={variant}
      data-size={size}
      data-loading={loading ? "true" : "false"}
      disabled={isDisabled}
      className={classes}
      {...rest}
    >
      {loading && (
        <span
          data-testid="g-button-spinner"
          aria-hidden="true"
          className="g-spin inline-block h-[15px] w-[15px] rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {!loading && leading && <Icon name={leading} size={iconPx[size]} />}
      <span>{children}</span>
      {!loading && trailing && <Icon name={trailing} size={iconPx[size]} />}
    </button>
  );
}
